import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import isMobile from '../../../../../components/IsMobile';
import Colors from '../../../../../constants/Colors';
import ReportTimeIcon from '../../../../../components/ReportTimeIcon';
import CompletedIcon from '../../../../../components/24pxIcons/CompletedOnIcon';
import StartedOnIcon from '../../../../../components/24pxIcons/StartedOnIcon';
import ReopenedOnIcon24 from '../../../../../components/24pxIcons/ReopenedOnIcon';
import SLATimeIcon from '../../../../../components/24pxIcons/SLATimeIcon';
import ResponseTimeIcon from '../../../../../components/24pxIcons/ResponseTimeIcon';
import TimeOnTaskIcon from '../../../../../components/24pxIcons/TimeOnTaskIcon';
import DueDateIcon from '../../../../../components/24pxIcons/DueDateIcon';
import Style from '../../../../../constants/Style';
import { Ticket, ticketPriorityIsValid } from '../../../../../constants/Types';
import { BuildingSettingsPriority, pad } from '../../../../LocationManagement/components/BuildingSettingsModal/data';
import { Building } from '../../../../LocationManagement/components/AddBuildingModal';

interface TimeSectionProps {
  selectedTicket: Ticket,
}

const TimeSection = ({ selectedTicket }: TimeSectionProps) => {
  const slaHours: BuildingSettingsPriority[] = useSelector((state:
    { selectedBuilding: Building }) => state.selectedBuilding.SlaHours);
  const [time, setTime] = useState<number>(new Date().getTime());
  const { priority, responseDueDate } = selectedTicket;
  const resolutionHours: number = selectedTicket.slaHours
    ?? priority ? slaHours[priority]?.ResolutionHours : null;
  const resolutionMinutes: number = selectedTicket.slaMinutes
    ?? priority ? slaHours[priority]?.ResolutionMinutes : null;
  const resolutionEstimateAvailable: boolean = !!resolutionHours || !!resolutionMinutes;
  const slaResponseHours: number | null = ticketPriorityIsValid(priority)
    ? slaHours[priority]?.ResponseHours : null;
  const slaResponseMinutes: number | null = ticketPriorityIsValid(priority)
    ? slaHours[priority]?.ResponseMinutes : null;
  const responseEstimateAvailable: boolean = !!slaResponseHours || !!slaResponseMinutes;
  const creationTime: number = selectedTicket?.createdOn
    ? new Date(selectedTicket.createdOn).getTime() : null;
  const timeElapsed: number = (selectedTicket?.completedOn
    ? new Date(selectedTicket.completedOn).getTime() : time) - creationTime;
  const startTime: number = selectedTicket?.startedOn
    ? new Date(selectedTicket.initialStartedOn ?? selectedTicket.startedOn).getTime() : 0;
  let timeOnTask: number = 0;
  const actualResponseTime: number = selectedTicket.startedOn ? startTime - creationTime : 0;
  const responseOverdue: boolean = new Date(responseDueDate).getTime() < startTime;
  const responseTimeExceeded: boolean = (slaResponseHours * 3600000
    + slaResponseMinutes * 60000) < actualResponseTime;

  if (selectedTicket.startedOn) {
    timeOnTask = (selectedTicket.completedOn
      ? new Date(selectedTicket.completedOn).getTime() : time) - startTime;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date().getTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return <>
    <Text style={styles.sectionTitle} selectable>{'Time and SLA'}</Text>
    <View style={isMobile && styles.detailSection}>
      <View style={[
        !isMobile && Style.row, styles.timeSectionItem, styles.firstTimeSectionItem,
      ]}>
        <View style={styles.row}>
          <ReportTimeIcon color={Colors.light.hereworksBlack400} />
          <Text style={styles.timeTitle} selectable>{'Report time: '}</Text>
        </View>
        <Text style={[styles.ticketDetail, styles.timeValue]} selectable>
          {Moment(new Date(selectedTicket.createdOn)).format('llll')}
        </Text>
      </View>
      <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
        <View style={styles.row}>
          <ResponseTimeIcon color={Colors.light.hereworksBlack400} />
          <Text style={styles.timeTitle} selectable>{'Response time: '}</Text>
        </View>
        <View style={!isMobile && Style.row}>
          {responseEstimateAvailable
            && <Text style={[styles.timeValue, styles.ticketDetail]} selectable>
              {`Target: ${pad(slaResponseHours)}:${pad(slaResponseMinutes)}:00\t\t`}
            </Text>}
          <View style={[Style.row, styles.timeValue]}>
            <Text style={styles.ticketDetail} selectable>
              {'Actual: '}
            </Text>
            <Text style={[
              styles.ticketDetail, styles.timeBlock,
              actualResponseTime > 0 && [
                selectedTicket.startedOn && styles.completedColor,
                (responseDueDate ? responseOverdue : responseTimeExceeded) && styles.exceededColor,
              ],
            ]} selectable>
              {actualResponseTime > 0 ? <>
                {`${pad(Math.floor(actualResponseTime / 3600000))}:`}
                {`${pad(Math.floor(actualResponseTime / 60000) % 60)}:`}
                {`${pad(Math.floor(actualResponseTime / 1000) % 60)}`}
              </> : '-'}
            </Text>
          </View>
        </View>
      </View>
      <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
        <View style={styles.row}>
          <SLATimeIcon color={Colors.light.hereworksBlack400} />
          <Text style={styles.timeTitle} selectable>{'Resolution time: '}</Text>
        </View>
        <View style={!isMobile && Style.row}>
          {resolutionEstimateAvailable
            && <Text style={[styles.timeValue, styles.ticketDetail, styles.slaTimeText]} selectable>
              {`Target: ${pad(resolutionHours)}:${pad(resolutionMinutes ?? 0)}:00\t\t`}
            </Text>}
          <View style={[Style.row, styles.timeValue]}>
            <Text style={[styles.ticketDetail, styles.slaTimeText]} selectable>
              {'Actual: '}
            </Text>
            <Text style={[
              styles.ticketDetail, styles.slaTimeText, styles.timeBlock,
              resolutionEstimateAvailable && timeElapsed > 0 && [
                selectedTicket.completedOn && styles.completedColor,
                (resolutionHours * 3600000 + resolutionMinutes * 60000) < timeElapsed
                && styles.exceededColor,
              ],
            ]} selectable>
              {timeElapsed > 0 ? <>
                {`${pad(Math.floor(timeElapsed / 3600000))}:`}
                {`${pad(Math.floor(timeElapsed / 60000) % 60)}:`}
                {pad(Math.floor(timeElapsed / 1000) % 60)}
              </> : '-'}
            </Text>
          </View>
        </View>
      </View>
      <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
        <View style={styles.row}>
          <TimeOnTaskIcon color={Colors.light.hereworksBlack400} />
          <Text style={styles.timeTitle} selectable>{'Time on task: '}</Text>
        </View>
        <Text style={[styles.ticketDetail, styles.timeValue]} selectable>
          {timeOnTask > 0 ? <>
            {pad(Math.floor(timeOnTask / 3600000))}
            {`:${pad(Math.floor(timeOnTask / 60000) % 60)}:`}
            {pad(Math.floor(timeOnTask / 1000) % 60)}
          </> : '-'}
        </Text>
      </View>
      {selectedTicket.startedOn
        && <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
          <View style={Style.row}>
            <StartedOnIcon color={Colors.light.hereworksBlack400} />
            <Text style={styles.timeTitle} selectable>{'Started on: '}</Text>
          </View>
          <Text style={[styles.ticketDetail, styles.timeValue]} selectable>
            {Moment(new Date(selectedTicket.startedOn))
              .format('llll')}
          </Text>
        </View>}
      <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
        <View style={styles.row}>
          {selectedTicket.completedOn
            ? <CompletedIcon color={styles.completedColor.color} /> : <DueDateIcon />}
          <Text style={[
            styles.timeTitle, selectedTicket.completedOn && styles.completedColor,
          ]} selectable>
            {selectedTicket.completedOn ? 'Completed on: ' : 'Due time: '}
          </Text>
        </View>
        <Text style={[
          styles.ticketDetail, styles.timeValue, styles.completedColor,
          (selectedTicket.completedOn
            ? new Date(selectedTicket.completedOn).getTime()
            > new Date(selectedTicket.dueDate).getTime()
            : (selectedTicket.dueDate
              && new Date(selectedTicket.dueDate).getTime() < time))
          && styles.exceededColor,
        ]} selectable>
          {Moment(new Date(selectedTicket.completedOn ?? selectedTicket.dueDate)).format('llll')}
        </Text>
      </View>
      {selectedTicket.initialStartedOn
        && <View style={[!isMobile && Style.row, styles.timeSectionItem]}>
          <View style={Style.row}>
            <ReopenedOnIcon24 color={Colors.light.hereworksBlack400} />
            <Text style={styles.timeTitle} selectable>{'Reopened on: '}</Text>
          </View>
          <Text style={[styles.ticketDetail, styles.timeValue]} selectable>
            {Moment(new Date(selectedTicket.startedOn)).format('llll')}
          </Text>
        </View>}
    </View>
  </>;
};

const styles = StyleSheet.create({
  detailSection: {
    paddingHorizontal: isMobile ? undefined : 12,
    paddingVertical: isMobile ? undefined : 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  sectionTitle: {
    marginTop: 16,
    marginBottom: isMobile ? 12 : 8,
    fontSize: 18,
    lineHeight: 22,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack500,
  },
  ticketDetail: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 21,
    color: Colors.light.hereworksPrimaryGrey900,
  },
  timeTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    marginStart: 8,
  },
  timeValue: isMobile ? {
    marginStart: 28,
    marginTop: 9,
    justifyContent: 'flex-start',
  } : {},
  timeSectionItem: isMobile ? {
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: Colors.light.hereworksBlue100,
  } : {
    height: 36,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue50,
  },
  timeBlock: isMobile ? {} : {
    width: 72,
    textAlign: 'right',
  },
  firstTimeSectionItem: isMobile ? {
    borderTopWidth: undefined,
  } : {},
  row: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
  },
  completedColor: {
    color: Colors.light.hereworksGreen600,
  },
  exceededColor: {
    color: Colors.light.hereworksUrgentRed,
  },
  slaTimeText: {
    marginVertical: 0,
  },
});

export default TimeSection;
